import React, { useState } from 'react';
import { Plus, Search, Zap, Edit2, Copy, Trash2, MoreVertical, Power, PowerOff, Filter } from 'lucide-react';
import {
  useButtonActions,
  useDeleteButtonAction,
  useCloneButtonAction,
  useUpdateButtonAction,
  getButtonTypeLabel,
  getLocationLabel,
  getActionTypeLabel,
  type ButtonAction
} from '../../../services/queries/buttonActionQueries';
import { USE_GOBODO_API } from '../../../utils/gobodoUtils';
import { DevModeBanner } from '../../common/DevModeBanner';
import { ButtonActionBuilder } from './ButtonActionBuilder';
import { useToast } from '../../../contexts/ToastContext';
import {
  StandardButton,
  StandardSearchBar,
  StandardSelect,
  StandardLoadingState,
  StandardErrorState,
  StandardEmptyState
} from '../../standard';

interface ButtonsActionsHomeProps {
  objectName: string;
  objectLabel: string;
  allFields: any[];
}

export const ButtonsActionsHome: React.FC<ButtonsActionsHomeProps> = ({
  objectName,
  objectLabel,
  allFields
}) => {
  const { success, error: showError } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<'all' | 'button' | 'quick-action'>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [builderOpen, setBuilderOpen] = useState<{ button?: ButtonAction; objectName: string } | null>(null);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);

  const { data: buttons, isLoading, error } = useButtonActions(objectName);
  const deleteButtonMutation = useDeleteButtonAction();
  const cloneButtonMutation = useCloneButtonAction();
  const updateButtonMutation = useUpdateButtonAction();

  const filteredButtons = buttons?.filter(button => {
    const matchesSearch = button.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         button.label.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = typeFilter === 'all' || button.type === typeFilter;

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && button.isActive) ||
                         (statusFilter === 'inactive' && !button.isActive);

    return matchesSearch && matchesType && matchesStatus;
  });

  const totalButtons = buttons?.length || 0;
  const customButtons = buttons?.filter(b => b.type === 'button').length || 0;
  const quickActions = buttons?.filter(b => b.type === 'quick-action').length || 0;
  const activeButtons = buttons?.filter(b => b.isActive).length || 0;

  const handleToggleActive = async (buttonId: string, currentStatus: boolean) => {
    try {
      await updateButtonMutation.mutateAsync({
        buttonId,
        updates: { isActive: !currentStatus }
      });
      setMenuOpen(null);
    } catch (error: any) {
      showError('Failed to update button status', error.message || 'Please try again');
    }
  };

  const handleDelete = async (buttonId: string, buttonName: string) => {
    if (!window.confirm(`Are you sure you want to delete "${buttonName}"?`)) return;

    try {
      await deleteButtonMutation.mutateAsync({ buttonId, objectName });
      success('Button deleted successfully!', `${buttonName} has been permanently removed`);
      setMenuOpen(null);
    } catch (error: any) {
      showError('Failed to delete button', error.message || 'Please try again');
    }
  };

  const handleClone = async (buttonId: string) => {
    try {
      const cloned = await cloneButtonMutation.mutateAsync(buttonId);
      setBuilderOpen({ button: cloned, objectName });
      setMenuOpen(null);
    } catch (error) {
      console.error('Failed to clone button:', error);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Buttons & Actions</h2>
            <p className="text-gray-600 dark:text-gray-300 mt-1">
              Create custom buttons and quick actions for {objectLabel} records
            </p>
          </div>
          <StandardButton
            variant="primary"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => setBuilderOpen({ objectName })}
          >
            New Button/Action
          </StandardButton>
        </div>

        {/* Development Mode Banner */}
        <DevModeBanner
          dataSource={USE_GOBODO_API ? 'Gobodo API' : 'mockDataManager.ts'}
          itemCount={buttons ? `${buttons.length} buttons & actions` : 'buttons & actions'}
          customMessage={USE_GOBODO_API
            ? `Gobodo: ${buttons ? `Showing ${buttons.length} buttons & actions` : 'Loading'} from Gobodo API. Changes persist to backend.`
            : `Development Mode: ${buttons ? `Showing ${buttons.length} buttons & actions` : 'Using mock data'} (in-memory).`}
          dismissible={true}
        />

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {totalButtons}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Total</div>
              </div>
              <div className="p-3 bg-purple-100 dark:bg-purple-900/20 rounded-lg">
                <Zap className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {customButtons}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Custom Buttons</div>
              </div>
              <div className="p-3 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <svg className="w-6 h-6 text-blue-600 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {quickActions}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Quick Actions</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <svg className="w-6 h-6 text-green-600 dark:text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">
                  {activeButtons}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-300">Active</div>
              </div>
              <div className="p-3 bg-green-100 dark:bg-green-900/20 rounded-lg">
                <Power className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <StandardSearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="Search buttons and actions..."
            />
          </div>

          <StandardSelect
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as any)}
            options={[
              { value: 'all', label: 'All Types' },
              { value: 'button', label: 'Custom Buttons' },
              { value: 'quick-action', label: 'Quick Actions' }
            ]}
          />

          <StandardSelect
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'active', label: 'Active Only' },
              { value: 'inactive', label: 'Inactive Only' }
            ]}
          />
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <StandardLoadingState message="Loading buttons & actions..." />
      )}

      {/* Error State */}
      {error && (
        <StandardErrorState
          title="Error loading buttons & actions"
          message="Please try again."
        />
      )}

      {/* Buttons List */}
      {!isLoading && !error && (
        <>
          {filteredButtons && filteredButtons.length > 0 ? (
            <div className="space-y-3">
              {filteredButtons.map((button) => (
                <ButtonCard
                  key={button.id}
                  button={button}
                  onEdit={() => setBuilderOpen({ button, objectName })}
                  onClone={() => handleClone(button.id)}
                  onDelete={() => handleDelete(button.id, button.name)}
                  onToggleActive={() => handleToggleActive(button.id, button.isActive)}
                  menuOpen={menuOpen === button.id}
                  onMenuToggle={() => setMenuOpen(menuOpen === button.id ? null : button.id)}
                />
              ))}
            </div>
          ) : (
            <StandardEmptyState
              icon={<Zap className="w-16 h-16" />}
              title={searchQuery || typeFilter !== 'all' || statusFilter !== 'all' ? 'No buttons or actions found' : 'No buttons or actions yet'}
              message={searchQuery || typeFilter !== 'all' || statusFilter !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first button or action to add custom functionality'
              }
              actionLabel={searchQuery || typeFilter !== 'all' || statusFilter !== 'all' ? 'Clear filters' : 'Create Button/Action'}
              onAction={searchQuery || typeFilter !== 'all' || statusFilter !== 'all'
                ? () => { setSearchQuery(''); setTypeFilter('all'); setStatusFilter('all'); }
                : () => setBuilderOpen({ objectName })
              }
            />
          )}
        </>
      )}

      {/* Button Action Builder Modal */}
      {builderOpen && (
        <ButtonActionBuilder
          button={builderOpen.button}
          objectName={builderOpen.objectName}
          objectLabel={objectLabel}
          allFields={allFields}
          onClose={() => setBuilderOpen(null)}
        />
      )}
    </div>
  );
};

// Button Card Component
interface ButtonCardProps {
  button: ButtonAction;
  onEdit: () => void;
  onClone: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  menuOpen: boolean;
  onMenuToggle: () => void;
}

const ButtonCard: React.FC<ButtonCardProps> = ({
  button,
  onEdit,
  onClone,
  onDelete,
  onToggleActive,
  menuOpen,
  onMenuToggle
}) => {
  const colorClasses = {
    purple: 'bg-purple-100 dark:bg-purple-900/20 text-purple-600 dark:text-purple-400',
    blue: 'bg-blue-100 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
    green: 'bg-green-100 dark:bg-green-900/20 text-green-600 dark:text-green-400',
    red: 'bg-red-100 dark:bg-red-900/20 text-red-600 dark:text-red-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/20 text-yellow-600 dark:text-yellow-400',
    gray: 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className={`p-3 rounded-lg flex-shrink-0 ${colorClasses[button.color || 'gray']}`}>
          <Zap className="w-6 h-6" />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h3 className="font-semibold text-gray-900 dark:text-white truncate">
                  {button.name}
                </h3>
                {button.isActive ? (
                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-xs rounded font-medium flex items-center gap-1">
                    <Power className="w-3 h-3" />
                    Active
                  </span>
                ) : (
                  <span className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-400 text-xs rounded font-medium flex items-center gap-1">
                    <PowerOff className="w-3 h-3" />
                    Inactive
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400 text-xs rounded font-medium">
                  {getButtonTypeLabel(button.type)}
                </span>
                <span className="px-2 py-0.5 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 text-xs rounded font-medium">
                  {getLocationLabel(button.location)}
                </span>
              </div>
            </div>

            <div className="relative ml-4">
              <button
                onClick={onMenuToggle}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              >
                <MoreVertical className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>

              {menuOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={onMenuToggle}
                  />
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-20">
                    <div className="py-1">
                      <button
                        onClick={onEdit}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Edit Button
                      </button>
                      <button
                        onClick={onToggleActive}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        {button.isActive ? (
                          <>
                            <PowerOff className="w-4 h-4" />
                            Deactivate
                          </>
                        ) : (
                          <>
                            <Power className="w-4 h-4" />
                            Activate
                          </>
                        )}
                      </button>
                      <button
                        onClick={onClone}
                        className="w-full px-4 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center gap-2"
                      >
                        <Copy className="w-4 h-4" />
                        Clone Button
                      </button>
                      <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
                      <button
                        onClick={onDelete}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                      >
                        <Trash2 className="w-4 h-4" />
                        Delete Button
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Button Preview */}
          <div className="mb-3">
            <button
              disabled
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 ${
                button.color === 'purple' ? 'bg-purple-600 text-white' :
                button.color === 'blue' ? 'bg-blue-600 text-white' :
                button.color === 'green' ? 'bg-green-600 text-white' :
                button.color === 'red' ? 'bg-red-600 text-white' :
                button.color === 'yellow' ? 'bg-yellow-600 text-white' :
                'bg-gray-600 text-white'
              } opacity-75 cursor-not-allowed`}
            >
              {button.icon && <span>🔧</span>}
              {button.label}
            </button>
          </div>

          {/* Action Info */}
          <div className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            <strong>Action:</strong> {getActionTypeLabel(button.action.type)}
            {button.action.type === 'call-api' && button.action.apiUrl && (
              <span className="ml-2 text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                {button.action.apiMethod} {button.action.apiUrl}
              </span>
            )}
            {button.action.type === 'update-fields' && button.action.fieldUpdates && (
              <span className="ml-2 text-xs">
                ({button.action.fieldUpdates.length} field{button.action.fieldUpdates.length !== 1 ? 's' : ''})
              </span>
            )}
            {button.action.type === 'navigate' && button.action.navigateUrl && (
              <span className="ml-2 text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                {button.action.navigateUrl}
              </span>
            )}
          </div>

          {/* Visibility Rules */}
          {button.visibilityRules.length > 0 && (
            <div className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
              <Filter className="w-3 h-3" />
              <span>
                {button.visibilityRules.length} visibility rule{button.visibilityRules.length !== 1 ? 's' : ''}
              </span>
            </div>
          )}

          {/* Metadata */}
          <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            Modified {new Date(button.lastModified).toLocaleDateString()} by {button.lastModifiedBy}
          </div>
        </div>
      </div>
    </div>
  );
};

